import { SelectionModel } from '@angular/cdk/collections';
import { ComponentType } from '@angular/cdk/portal';
import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { FileCode } from '@core/enums/file-code';
import { FileType } from '@core/enums/file-type';
import { TaskType } from '@core/enums/task-type';
import { TreeItemType } from '@core/enums/tree-item-type';
import { FileHelper } from '@core/helpers/file.helper';
import { FileInfo } from '@core/interfaces/file-info';
import { DocViewerModalInput } from '@core/interfaces/modal-inputs/doc-viewer-modal-input';
import { TaskFilesService } from '@web/services/task-files.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { DocViewerModalComponent } from '@shared/components/doc-viewer-modal/doc-viewer-modal.component';
import { DialogService, SnackBarService } from 'osc-reference-app-common';
import { Subscription, firstValueFrom } from 'rxjs';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { MatButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { CustomDatePipe } from '@shared/pipes/custom-date.pipe';
import { OscSpinnerComponent } from '@osc-utils/components/osc-spinner/osc-spinner.component';
import { ConfigurationService } from '@osc-utils/services/configuration.service';
import { FileListService } from './file-list.service';

@Component({
  selector: 'app-file-list',
  templateUrl: './file-list.component.html',
  styleUrls: ['./file-list.component.scss'],
  providers: [FileListService],
  imports: [
    MatButton,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCheckbox,
    MatCellDef,
    MatCell,
    MatSortHeader,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    OscSpinnerComponent,
    CustomDatePipe,
    TranslateModule,
  ],
})
export class FileListComponent implements OnDestroy, OnInit, AfterViewInit {
  #route = inject(ActivatedRoute);
  #taskFilesService = inject(TaskFilesService);
  #dialogService = inject(DialogService);
  #snackBarService = inject(SnackBarService);
  #fileListService = inject(FileListService);
  #configurationService = inject(ConfigurationService);
  #translate = inject(TranslateService);

  @Input() public itemType: TreeItemType;
  @Input() public projectId: string;
  @Input() public jobId: string;
  @Input() public taskId: string;
  @Input() public projectName: string;
  @Input() public taskName: string;
  @ViewChild('filesSpinner') public filesSpinner: OscSpinnerComponent;
  public columns: string[] = ['Select', 'fileName', 'updatedOnUtc'];

  public filesDataSource: MatTableDataSource<FileInfo>;

  public files: Array<FileInfo>;
  public item: string;

  public TreeItemType = TreeItemType;
  public FileType = FileType;
  public TaskType = TaskType;
  public filesSpinnerId = 'filesSpinner';
  public dataLoaded: boolean;

  #fileSelection = new SelectionModel<FileInfo>(true, []);
  #inspectionFileSelection = new SelectionModel<FileInfo>(true, []);
  #spinnerSubscription: Subscription;
  #networkChangeSubscription: Subscription;

  @ViewChild('filesSorter') public set sort(sort: MatSort) {
    this.filesDataSource.sort = sort;
  }

  public ngOnInit() {
    this.#route.params.subscribe(async params => {
      if (params.projectId && params.jobId && params.taskId) {
        this.projectId = params.projectId;
        this.jobId = params.jobId;
        this.taskId = params.taskId;
      }
      this.setLabel();
      this.clearData();

      /*
        Due to report pdf get updated frequently with auto-saving,
        always get latest pdf URL instead of from cache.
      */
      this.#taskFilesService.fetchTaskFiles(this.projectId, this.jobId, this.taskId).subscribe(taskFiles => {
        this.setMatTableDataSource(taskFiles.allDesc);
      });
    });
  }

  public ngAfterViewInit() {
    if (this.filesSpinner) {
      this.#spinnerSubscription = this.filesSpinner?.spinnerTimeout?.subscribe(err => {
        if (err === '') {
          err = this.#translate.instant('fileList.ngAfterViewInitError');
        }
        this.#snackBarService.error(err);
      });
    }
    if (this.#configurationService.isMobileApp() && this.itemType === TreeItemType.Task) {
      this.columns.shift();
    }
  }

  public ngOnDestroy(): void {
    this.#spinnerSubscription?.unsubscribe();
    this.#networkChangeSubscription?.unsubscribe();
  }

  public open(file: FileInfo): void {
    const input: DocViewerModalInput = {
      id: file.id,
      url: file.downloadUri.split('?')[0],
      title: file.fileName,
      updatedOnUtc: file.updatedOnUtc,
    };
    this.#dialogService.openFullScreenDialog<DocViewerModalComponent, DocViewerModalInput, void>(
      DocViewerModalComponent as ComponentType<DocViewerModalComponent>,
      input,
    );
  }

  public download(files?: FileInfo[]): void {
    FileHelper.downloadFiles(files == null ? this.getAllSelected() : files);
  }

  public async downloadZip() {
    const filesToDownload = this.getAllSelected();

    for (const f of filesToDownload) {
      if (!f.downloadUri.includes('?')) {
        continue;
      }
      f.downloadUri = FileHelper.removeQueryPartFromUri(f.downloadUri);
    }

    if (filesToDownload.length > 1) {
      await this.downloadZipFiles(filesToDownload);
      return;
    }

    this.download(filesToDownload);
  }

  public isAllSelected(fileType: FileType) {
    const dataSource = this.getDataSource(fileType);
    if (!dataSource) {
      return false;
    }

    const numSelected = this.getSelection(fileType).selected.length;
    const numRows = dataSource.data.length;
    return numSelected === numRows;
  }

  public masterToggle(fileType: FileType) {
    if (this.isAllSelected(fileType)) {
      this.getSelection(fileType).clear();
    } else {
      this.getDataSource(fileType).data.forEach(row => this.getSelection(fileType).select(row));
    }
  }

  public checkboxLabel(fileType: FileType, row?: FileInfo): string {
    if (!row) {
      return `${
        this.isAllSelected(fileType)
          ? this.#translate.instant('fileList.checkboxLabelSelect')
          : this.#translate.instant('fileList.checkboxLabelDeselect')
      } all`;
    }
    return `${
      this.getSelection(fileType).isSelected(row)
        ? this.#translate.instant('fileList.checkboxLabelDeselect')
        : this.#translate.instant('fileList.checkboxLabelSelect')
    } row ${row.id}`;
  }

  public getFileTypeDisplayName(fileType: string, fileName: string): string {
    switch (fileType) {
      case FileCode.FILE_FROM_BUILDER: {
        return this.#translate.instant('fileList.getFileTypeDisplayNameFileFromBuilder');
      }
      case FileCode.CMLMAP: {
        return this.#translate.instant('fileList.getFileTypeDisplayNameCmlMap');
      }
      case FileCode.ITP: {
        return this.#translate.instant('fileList.getFileTypeDisplayNameItp');
      }
      default: {
        const fileNameSegments: Array<string> = fileName.split('.');
        return fileNameSegments.length > 1 ? fileNameSegments[fileNameSegments.length - 1] : '';
      }
    }
  }

  public downloadBtnDisabled(): boolean {
    return this.getAllSelected().length === 0;
  }

  public selectionHasValue(fileType: FileType): boolean {
    return this.getSelection(fileType).hasValue();
  }

  public selectionToggle(fileType: FileType, row: FileInfo): void {
    this.getSelection(fileType).toggle(row);
  }

  public isSelected(fileType: FileType, row: FileInfo): boolean {
    return this.getSelection(fileType).isSelected(row);
  }

  public async downloadZipFiles(files: Array<FileInfo>) {
    const uriFiles = files.map(file => file.downloadUri);
    await this.createZip(uriFiles, this.projectName + '_' + this.taskName);
  }

  public async createZip(files: any[], zipName: string) {
    const zip = new JSZip();
    const name = zipName + '.zip';
    for (const file of files) {
      const element = file;
      const fileData: any = await firstValueFrom(this.#fileListService.fetchFile(element));
      const b: any = new Blob([fileData], { type: '' + fileData.type + '' });
      zip.file(element.substring(element.lastIndexOf('/') + 1), b);
    }
    zip.generateAsync({ type: 'blob' }).then(content => {
      saveAs(content, name);
    });
  }

  private setMatTableDataSource(files: FileInfo[]) {
    this.files = files.filter(
      file => file.fileType !== FileCode.FILE_FROM_DEVICE && file.fileType !== FileCode.FILE_FROM_BUILDER && file.isVisible,
    );
    this.files.map(file => (file.extension = this.getFileTypeDisplayName(file.fileType, file.fileName)));
    this.filesDataSource = new MatTableDataSource(this.files);
    this.filesDataSource.sort = this.sort;
    this.dataLoaded = true;
  }

  private getDataSource(fileType: FileType): MatTableDataSource<FileInfo> {
    switch (fileType) {
      case FileType.File: {
        return this.filesDataSource;
      }
    }
  }

  private getSelection(fileType: FileType): SelectionModel<FileInfo> {
    switch (fileType) {
      case FileType.File: {
        return this.#fileSelection;
      }
      case FileType.SurveyFile: {
        return this.#inspectionFileSelection;
      }
    }
  }

  private clearData(): void {
    this.#fileSelection = new SelectionModel<FileInfo>(true, []);
    this.#inspectionFileSelection = new SelectionModel<FileInfo>(true, []);
    this.filesDataSource = new MatTableDataSource([]);
  }

  private getAllSelected() {
    return this.#fileSelection.selected.concat(this.#inspectionFileSelection.selected);
  }

  private setLabel() {
    this.item = TreeItemType.Task.toLocaleUpperCase();
  }
}
